import React from "react";
import { connect, mapProps } from "@formily/react";
import { usePrefixCls } from "@formily/antd-v5/lib/__builtins__";
import { theme } from "antd";
import clsx from "clsx";

interface RumbleBoxSelectOption {
  label: React.ReactNode;
  value: string | number;
  image?: string;
}
export type RumbleBoxSelectOptions = RumbleBoxSelectOption[];

interface RumbleBoxSelectProps {
  value?: string | number;
  options: RumbleBoxSelectOptions;
  onChange?: (value: string | number) => void;
}

const RumbleBoxSelectBase: React.FC<RumbleBoxSelectProps> = ({
  value = "",
  options = [],
  onChange = () => {},
}) => {
  const prefixCls = usePrefixCls("rumble-box-select");
  const { token } = theme.useToken();

  if (
    !Array.isArray(options) ||
    options.length === 0 ||
    !options.every((option) => option.label && option.value)
  ) {
    console.warn(
      'RumbleBoxSelect: options must be an array of objects with at least \"label\" and \"value\" properties.',
    );
    return null;
  }

  const isSelected = (val: string | number) => value === val;

  return (
    <div
      className={prefixCls}
      role="listbox"
      aria-label="Vervangend vervoer"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            role="option"
            aria-selected={selected}
            tabIndex={0}
            className={clsx(`${prefixCls}__option`, {
              [`${prefixCls}__option--selected`]: selected,
            })}
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              cursor: "pointer",
              border: "2px solid",
              borderRadius: 8,
              padding: 16,
              minWidth: 150,
              textAlign: "center",
              borderColor: selected ? token.colorPrimary : token.colorBorder,
              background: "#fff",
              boxShadow: selected ? "0 2px 8px rgba(0,0,0,0.08)" : undefined,
              transition: "border-color 0.2s, box-shadow 0.2s",
              outline: selected
                ? `2px solid ${token.colorPrimary}`
                : "2px solid transparent",
            }}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onChange(opt.value);
              }
            }}
          >
            {opt.image && (
              <img
                src={opt.image}
                alt={String(opt.label)}
                style={{
                  maxWidth: 220,
                  maxHeight: 200,
                  marginBottom: 8,
                  borderRadius: 4,
                  objectFit: "cover",
                  boxShadow: selected
                    ? "0 1px 4px rgba(0,0,0,0.09)"
                    : undefined,
                  background: "#fafafa",
                }}
              />
            )}
            <div
              className={`${prefixCls}__label`}
              style={{
                fontWeight: selected ? 600 : 400,
                color: selected ? token.colorPrimary : "inherit",
                marginTop: 4,
              }}
            >
              {opt.label}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export const RumbleBoxSelect = connect(
  RumbleBoxSelectBase,
  mapProps({
    dataSource: "options",
  }),
);

export default RumbleBoxSelect;
